import React, { useState, useEffect } from 'react';
import { LogOut, User, Home, Calendar, Vote, BarChart3, Archive, HelpCircle, Crown, Building2, FileText, ClipboardList, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getSistemaInfo } from '../utils/sistemaInfo';

interface HeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  temEmpresas?: boolean;
}

const Header: React.FC<HeaderProps> = ({ activeTab, setActiveTab, temEmpresas = true }) => {
  const { user, logout, empresaSelecionada } = useAuth();
  
  // Obter informações do sistema dinamicamente (priorizando tipo_comissao do banco)
  const tipoComissao = (empresaSelecionada as any)?.tipo_comissao;
  const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
  const nomeEmpresa = (empresaSelecionada as any)?.nome;
  
  const [sistemaInfo, setSistemaInfo] = useState({
    nome: 'CIPA FÁCIL',
    nomeCompleto: 'CIPA FÁCIL EHS',
    descricao: 'Sistema Completo de Gestão CIPA - 100% Conforme NR-05',
    descricaoCurta: 'Sistema 100% Conforme NR-05 Vigente',
    tipo: 'CIPA' as 'CIPA' | 'CIPAMIN' | 'CIPATR',
    norma: 'NR-05'
  });

  useEffect(() => {
    getSistemaInfo(cnaeEmpresa, tipoComissao, nomeEmpresa).then(setSistemaInfo);
  }, [cnaeEmpresa, tipoComissao, nomeEmpresa]);

  const handleLogout = async () => {
    if (window.confirm('Tem certeza que deseja sair do sistema?')) {
      await logout();
    }
  };

  const TabButton: React.FC<{ id: string; icon: React.ComponentType<{ className?: string }>; label: string; active: boolean; onClick: (id: string) => void; disabled?: boolean }> = ({ id, icon: Icon, label, active, onClick, disabled = false }) => (
    <button
      onClick={() => !disabled && onClick(id)}
      disabled={disabled}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
        disabled
          ? 'text-gray-400 cursor-not-allowed'
          : active 
            ? 'bg-blue-600 text-white shadow-lg' 
            : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden md:inline">{label}</span>
    </button>
  );

  return (
    <div>
      <nav className="bg-white shadow-lg border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/logo/logoEhsFacil.png" 
                alt="CIPA FÁCIL EHS Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-xl font-bold text-gray-800">{sistemaInfo.nomeCompleto}</h1>
            </div>
            
            <div className="flex items-center gap-4">
              {user && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800">
                      {user.nome_completo}
                    </p>
                    <p className="text-xs text-gray-600">
                      {user.email}
                    </p>
                    <p className="text-xs text-blue-600 font-medium">
                      {user.tipo_usuario === 'BASICO' && 'Básico'}
                      {user.tipo_usuario === 'PROFISSIONAL' && 'Profissional'}
                      {user.tipo_usuario === 'ENTERPRISE' && 'Enterprise'}
                      {user.tipo_usuario === 'SUPER_ADMIN' && 'Super Admin'}
                    </p>
                  </div>
                  <div className="bg-blue-100 p-2 rounded-full">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Sair do sistema"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden md:inline">Sair</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 flex-wrap py-4">
            <TabButton
              id="dashboard"
              icon={Home}
              label="Dashboard"
              active={activeTab === 'dashboard'}
              onClick={setActiveTab}
              disabled={!temEmpresas}
            />
            <TabButton
              id="cronograma"
              icon={Calendar}
              label="Cronograma"
              active={activeTab === 'cronograma'}
              onClick={setActiveTab}
              disabled={!temEmpresas}
            />
            <TabButton
              id="votacao"
              icon={Vote}
              label="Votação"
              active={activeTab === 'votacao'}
              onClick={setActiveTab}
              disabled={!temEmpresas}
            />
            <TabButton
              id="relatorios"
              icon={BarChart3}
              label="Relatórios"
              active={activeTab === 'relatorios'}
              onClick={setActiveTab}
              disabled={!temEmpresas}
            />
            {user?.tipo_usuario === 'ENTERPRISE' && (
              <TabButton
                id="plano-trabalho"
                icon={FileText}
                label="Plano de Trabalho"
                active={activeTab === 'plano-trabalho'}
                onClick={setActiveTab}
                disabled={!temEmpresas}
              />
            )}
            {user?.tipo_usuario === 'ENTERPRISE' && (
              <TabButton
                id="ata-digital"
                icon={ClipboardList}
                label="Ata Digital"
                active={activeTab === 'ata-digital'}
                onClick={setActiveTab}
                disabled={!temEmpresas}
              />
            )}
            <TabButton
              id="documentos"
              icon={Archive}
              label="Documentos"
              active={activeTab === 'documentos'}
              onClick={setActiveTab}
              disabled={!temEmpresas}
            />
            <TabButton
              id="ajuda"
              icon={HelpCircle}
              label="Ajuda"
              active={activeTab === 'ajuda'}
              onClick={setActiveTab}
              disabled={!temEmpresas}
            />
            {/* Aba de empresas para todos os perfis */}
            <TabButton
              id="empresas"
              icon={Building2}
              label="Minhas Empresas"
              active={activeTab === 'empresas'}
              onClick={setActiveTab}
            />
            {user?.tipo_usuario === 'SUPER_ADMIN' && (
              <TabButton
                id="admin"
                icon={Crown}
                label="Administração"
                active={activeTab === 'admin'}
                onClick={setActiveTab}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
